import { PLANE_LABELS, JOINT_LABELS } from './drives'
import { MOMENT_JOINTS, planeMoments, type MomentJoint } from './dynamics'
import { SIDES, sampleAngle, type Side } from './gait'
import { PLANES, type PlaneKey } from './joints'
import { differentiate, stats, type Stats } from './channels'
import type { Simulation } from './simulation'

/**
 * Das Verzeichnis aller Signale, die ein Durchlauf hergibt.
 *
 * WARUM ES DAS BRAUCHT. Der Kern rechnet mehrere hundert Reihen — Winkel,
 * Geschwindigkeiten, Momente, Leistungen, Bodenkräfte, je Gelenk, Ebene und
 * Seite. Welche davon es WIRKLICH gibt, hängt am Modell und an der Bewegung:
 * eine Achse ohne Winkelfreiheit liefert kein Moment, ein gerechneter Gang
 * kein Zehengrundgelenk. Bis hierher stand diese Auskunft nirgends; man hat sie
 * durch Ausprobieren gefunden, indem ein Diagramm leer blieb.
 *
 * DAS VERZEICHNIS WIRD ERZEUGT UND NICHT GEPFLEGT. Es fragt den Durchlauf, was
 * er hat, statt eine Liste zu führen, die daneben veraltet. Wer eine Grösse in
 * den Kern einbaut, findet sie hier ohne Zutun; wer eine entfernt, sieht sie
 * verschwinden.
 *
 * ES RECHNET NICHTS NACH. Jede Reihe wird aus dem Ergebnis GELESEN — bis auf
 * die Ableitungen, und die kommen aus derselben Funktion wie die Diagramme.
 * Zwei Wege zu derselben Zahl wären zwei Wahrheiten.
 */

/** Woher ein Signal kommt — der Modulname des Kerns. */
export type SignalSource =
  | 'Gang'
  | 'Bodenkontakt'
  | 'Dynamik'
  | 'Antriebe'
  | 'Antriebsstrang'

export interface Signal {
  key: string
  group: string
  label: string
  unit: string
  source: SignalSource
  /** Die Reihe selbst. */
  values: Float32Array
  /** Kennwerte über den Zyklus. */
  stats: Stats
  /**
   * Trägt die Reihe den Schlusspunkt mit?
   *
   * ZWEI KONVENTIONEN IM KERN, und sie nebeneinander zu sehen ist die halbe
   * Berechtigung dieser Seite. Winkelbahnen und Momente führen 121 Werte für
   * 120 Schritte — der letzte gleich dem ersten. Bodenkraft, Lastanteil und
   * Leistung führen 120. Wer die Reihenlänge für die Periode hält, tastet die
   * einen um ein Hundertzwanzigstel langsamer ab als die anderen.
   */
  closed: boolean
}

export interface SignalCatalog {
  signals: Signal[]
  /** Stützstellen je Zyklus — die PERIODE, nicht die Reihenlänge. */
  samples: number
  groups: string[]
}

const sideLabel: Record<Side, string> = { R: 'rechts', L: 'links' }

/**
 * Liest alles aus, was der Durchlauf hergibt.
 *
 * Die Reihenfolge folgt der Kette des Kerns: erst was der Gang vorgibt, dann
 * was der Bodenkontakt daraus macht, dann die Kinetik, dann die Antriebe. Wer
 * die Liste von oben liest, liest die Rechnung.
 */
export function signalCatalog(simulation: Simulation): SignalCatalog {
  const { gait, ground, dynamics, drives, power } = simulation
  const n = dynamics.samples
  const out: Signal[] = []

  const add = (
    key: string,
    group: string,
    label: string,
    unit: string,
    source: SignalSource,
    values: Float32Array,
  ) => {
    /* Eine Reihe, die überall null ist, ist keine Auskunft — sie ist eine
       Achse, die es in dieser Bewegung nicht gibt. Sie zu führen hiesse, ein
       leeres Diagramm anzubieten. */
    const s = stats(values, n)
    if (s.peak < 1e-9) return
    out.push({ key, group, label, unit, source, values, stats: s, closed: values.length > n })
  }

  /* --- Gang: die vorgegebenen Winkel ------------------------------------- */
  for (const joint of Object.keys(gait.angles)) {
    for (const plane of Object.keys(gait.angles[joint] ?? {}) as PlaneKey[]) {
      for (const side of SIDES) {
        const track = gait.angles[joint]?.[plane]?.[side]
        if (!track) continue
        const name = `${JOINT_LABELS[joint] ?? joint} · ${PLANE_LABELS[plane]} ${sideLabel[side]}`
        add(`A|${joint}|${plane}|${side}`, 'Winkel', name, '°', 'Gang', track)

        /* Aus DERSELBEN Funktion wie die Diagramme. Eine zweite Ableitung an
           einer zweiten Stelle wäre eine zweite Wahrheit. */
        const derived = differentiate(track, n, dynamics.cycleTime)
        add(
          `V|${joint}|${plane}|${side}`,
          'Winkelgeschwindigkeit',
          name,
          '°/s',
          'Gang',
          derived.velocity,
        )
        add(
          `C|${joint}|${plane}|${side}`,
          'Winkelbeschleunigung',
          name,
          '°/s²',
          'Gang',
          derived.acceleration,
        )
      }
    }
  }

  /* --- Bodenkontakt ------------------------------------------------------ */
  add('B|lift', 'Bodenkontakt', 'Höhe des Beckens über der Ruhelage', 'm', 'Bodenkontakt', ground.lift)
  add('B|forward', 'Bodenkontakt', 'Vorwärtsversatz des Beckens', 'm', 'Bodenkontakt', ground.forward)
  add('B|lateral', 'Bodenkontakt', 'Seitliche Verlagerung', 'm', 'Bodenkontakt', ground.lateral)
  for (const side of SIDES) {
    add(
      `B|contact|${side}`,
      'Bodenkontakt',
      `Bodenkontakt ${sideLabel[side]}`,
      '',
      'Bodenkontakt',
      ground.contact[side],
    )
  }

  /* --- Dynamik ------------------------------------------------------------ */
  const vec = (get: (i: number) => number): Float32Array => {
    const values = new Float32Array(n)
    for (let i = 0; i < n; i++) values[i] = get(i)
    return values
  }

  add('D|com|y', 'Schwerpunkt', 'Höhe des Schwerpunkts', 'm', 'Dynamik', vec((i) => dynamics.com[i].y))
  add('D|com|x', 'Schwerpunkt', 'Schwerpunkt vorwärts', 'm', 'Dynamik', vec((i) => dynamics.com[i].x))
  add('D|com|z', 'Schwerpunkt', 'Schwerpunkt seitlich', 'm', 'Dynamik', vec((i) => dynamics.com[i].z))
  add(
    'D|coma|y',
    'Schwerpunkt',
    'Schwerpunktbeschleunigung senkrecht',
    'm/s²',
    'Dynamik',
    vec((i) => dynamics.comAcceleration[i].y),
  )
  add('D|zmp|x', 'Schwerpunkt', 'Kraftangriffszentrum vorwärts', 'm', 'Dynamik', vec((i) => dynamics.zmp[i].x))
  add('D|zmp|z', 'Schwerpunkt', 'Kraftangriffszentrum seitlich', 'm', 'Dynamik', vec((i) => dynamics.zmp[i].z))

  for (const side of SIDES) {
    add(
      `G|v|${side}`,
      'Bodenreaktion',
      `Senkrecht ${sideLabel[side]}`,
      'N',
      'Dynamik',
      vec((i) => dynamics.grf[side][i].y),
    )
    add(
      `G|ap|${side}`,
      'Bodenreaktion',
      `Vorwärts ${sideLabel[side]}`,
      'N',
      'Dynamik',
      vec((i) => dynamics.grf[side][i].x),
    )
    add(
      `G|ml|${side}`,
      'Bodenreaktion',
      `Seitlich ${sideLabel[side]}`,
      'N',
      'Dynamik',
      vec((i) => dynamics.grf[side][i].z),
    )
    add(
      `L|${side}`,
      'Bodenreaktion',
      `Lastanteil ${sideLabel[side]}`,
      '',
      'Dynamik',
      vec((i) => dynamics.load[side][i]),
    )
    add(
      `C|cop|${side}`,
      'Bodenreaktion',
      `Druckmittelpunkt ${sideLabel[side]}, vorwärts`,
      'm',
      'Dynamik',
      vec((i) => dynamics.cop[side][i].x),
    )
  }

  for (const joint of MOMENT_JOINTS) {
    for (const side of SIDES) {
      const moments = planeMoments(dynamics, joint as MomentJoint, side)
      for (const plane of Object.keys(PLANES) as PlaneKey[]) {
        const name = `${JOINT_LABELS[joint] ?? joint} · ${PLANE_LABELS[plane]} ${sideLabel[side]}`
        add(`M|${joint}|${plane}|${side}`, 'Moment', name, 'Nm', 'Dynamik', moments[plane])
      }
      const name = `${JOINT_LABELS[joint] ?? joint} ${sideLabel[side]}`
      add(`P|${joint}|${side}`, 'Gelenkleistung', name, 'W', 'Dynamik', moments.power)
      add(`E|${joint}|${side}`, 'Gelenkleistung', `${name} · Energie`, 'J', 'Dynamik', moments.energy)
    }
  }

  /* --- Antriebe und Antriebsstrang --------------------------------------- */
  for (const drive of drives) {
    const d = drive.demand
    const name = d.label
    add(
      `X|${d.joint}|${d.plane}|${d.side}`,
      'Achsleistung',
      name,
      'W',
      'Antriebe',
      d.powerTrack,
    )
  }
  for (const joint of power.joints) {
    add(
      `NP|${joint.joint}|${joint.side}`,
      'Antriebsstrang',
      `${joint.label} · was der Akku liefern muss`,
      'W',
      'Antriebsstrang',
      joint.net,
    )
  }
  add('NP|net', 'Antriebsstrang', 'Akkuleistung gesamt', 'W', 'Antriebsstrang', power.net)
  add('NP|hold', 'Antriebsstrang', 'Halte- und Grundlast', 'W', 'Antriebsstrang', power.holding)

  const groups: string[] = []
  for (const signal of out) if (!groups.includes(signal.group)) groups.push(signal.group)

  return { signals: out, samples: n, groups }
}

/** Nur zur Gegenprobe: wie viele Signale eine Gruppe hat. */
export const countByGroup = (catalog: SignalCatalog): Record<string, number> => {
  const out: Record<string, number> = {}
  for (const signal of catalog.signals) out[signal.group] = (out[signal.group] ?? 0) + 1
  return out
}

/** Nur zur Gegenprobe der Winkelabfrage — dieselbe Quelle wie die Kurven. */
export const angleAt = sampleAngle
